import React, { useState } from "react";
import './Registration.scss';
import {
  Stack,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Alert,
  Typography,
  Paper,
  Avatar,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useDispatch, useSelector } from "react-redux";
import { registerUser } from "../../slices/userSlice";
import { useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import PersonIcon from "@mui/icons-material/Person";
import Tooltip from "@mui/material/Tooltip";
import FilterRoundedIcon from '@mui/icons-material/FilterRounded';

export default function Registration() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector((state) => state.user.loading);
 
  // State for form and success screen
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
 
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [clientError, setClientError] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [profilePictureURL, setProfilePictureURL] = useState('');
  const [isPhotoModalOpen, setPhotoModalOpen] = useState(false);
  const [tempProfile, setTempProfile] = useState('');

  // validation
  const validateField = (name, value) => {
    switch (name) {
      case 'email':
        if (value.length > 255) return "Email must be at most 255 characters"; 
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Invalid email format";
        return "";
      case 'firstName':
      case 'lastName':
        if (value.trim() === "") return "This field is required";
        if (value.length > 50) return "Maximum 50 characters allowed";
        return "";
      case 'phoneNumber':
        if (value.length > 14) return "Phone Number must be at most 10 characters";    
        const cleanValue = value.replace(/[\(\)\-\s]/g, "");
        const phoneRegex = /[^\d]/g;
        return phoneRegex.test(value) ? "" : "Invalid input";
      case 'profilePictureURL':
        if (!value || value.trim() === "") return "";
        if (value.length === 2000) return "Maximum 2000 characters allowed";
        try { new URL(value); return ""; } catch (_) { return "Must be a valid URL"; }
      default:
        return "";}
  
  };
  const validatePassword = (pass) => {
    const errors = [];
    if (pass.length < 8 || pass.length > 64) errors.push("8-64 characters");
    if (!/[A-Z]/.test(pass)) errors.push("one uppercase letter");
    if (!/[a-z]/.test(pass)) errors.push("one lowercase letter");
    if (!/[0-9]/.test(pass)) errors.push("one digit");
    if (!/[!@#$%^&*.?]/.test(pass)) errors.push("one special character");
    return errors;
  };
  const formatPhoneNumber = (value) => {
    if (!value) return value;

    const phoneNumber = value.replace(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;

    if (phoneNumberLength < 4) return phoneNumber;
    
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0,3)}) ${phoneNumber.slice(3)}`
    }

    return `(${phoneNumber.slice(0,3)}) ${phoneNumber.slice(3,6)}-${phoneNumber.slice(6,10)}`
  }

  // Form validation
  const isFormValid =
    firstName.trim() !== "" &&
    lastName.trim() !== "" &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
    validatePassword(password).length === 0 &&
    password === confirmPassword;

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: ""
  });

  // Password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
 
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);
  const handleMouseDownPassword = (e) => e.preventDefault();
 
  const passwordAdornment = (
    <InputAdornment position="end">
      <IconButton
        aria-label={showPassword ? 'Hide password' : 'Show password'}
        onClick={handleClickShowPassword}
        onMouseDown={handleMouseDownPassword}
        edge="end"
        data-testid="toggle-password-visibility"
      >
        {showPassword ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  const confirmPasswordAdornment = (
    <InputAdornment position="end">
      <IconButton
        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
        onClick={handleClickShowConfirmPassword}
        onMouseDown={handleMouseDownPassword}
        edge="end"
        data-testid="toggle-confirm-password-visibility"
      >
        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  const formatServerError = (err) => {
    let raw = err?.payload?.error || err?.error || (typeof err === "string" ? err : "");
    if (!raw) return "Something went wrong";

    let originalMessage = String(raw).toLowerCase();

    if (originalMessage.includes("already exists") && originalMessage.includes("email")) {
      return "This email is already registered. Please use a different email or log in.";
    }

    return "An error occurred. Please try again.";
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setClientError("");
    

    const userData = {
      "firstName": firstName,
      "lastName": lastName,
      "email": email.toLowerCase().trim(),
      "password": password,
      "phoneNumber": phoneNumber.replace(/\D/g, "") || null,
      "profilePictureURL":  profilePictureURL.trim() || null
    };

    const result = await dispatch(registerUser(userData));
 
    if (registerUser.rejected.match(result)) {
      const cleanError = formatServerError(result.payload || result.error);
      setClientError(cleanError);
    }
    if (registerUser.fulfilled.match(result)) {
      setRegistrationSuccess(true); // Show confirmation screen
    }
  };

  const handleOpenPhotoModal = () => {
    setTempProfile(profilePictureURL);
    setPhotoModalOpen(true);
  }
  
  const handleClosePhotoModal = () => {
    setTempProfile("");
    setErrors(prev => ({ prev, profilePictureURL: "" }));
    setPhotoModalOpen(false);
  }

  const handleAvatar = () => {
    if(!tempProfile || tempProfile.trim() ===""){
      setProfilePictureURL("");
      setPhotoModalOpen(false);
      return;
    }
    const validError = validateField('profilePictureURL',tempProfile);
    if(validError){
      toast.error(validError);
    }else{
      setProfilePictureURL(tempProfile)
      toast.success("Avatar URL is valid.")
      setPhotoModalOpen(false);
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "firstName") setFirstName(value);
    if (name === "lastName") setLastName(value);
    if (name === "email") setEmail(value);
    if (name === "phoneNumber") setPhoneNumber(value);
    if (name === "profilePictureURL") setProfilePictureURL(value);

    setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    if (name === "password") {
      setPassword(value);
    } else {
      setConfirmPassword(value);
    }

    const targetPass = name === "password" ? value : password;
    const targetConfirm = name === "confirmPassword" ? value : confirmPassword;
    
    const issues = validatePassword(targetPass);
    const passErr = (targetPass.length > 0 && issues.length > 0) 
      ? `Needs: ${issues.join(", ")}` 
      : "";

    const matchErr = (targetConfirm.length > 0 && targetPass !== targetConfirm)
      ? "Passwords do not match"
      : "";

    setErrors(prev => ({
      ...prev,
      password: passErr,
      confirmPassword: matchErr
    }));
  };

  // Hide IE password reveal/clear buttons
  if (typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.textContent = `
      input[type="password"]::-ms-reveal,
      input[type="password"]::-ms-clear {
        display: none;
      }
    `;
    document.head.appendChild(style);
  }

  // Confirmation Screen
  if (registrationSuccess) {
    return (
      <Stack
        spacing={4}
        sx={{ maxWidth: 400, margin: 'auto', padding: 4, textAlign: 'center', wordBreak: 'break-word' }}
        component={Paper}
        elevation={4}
        data-testid="registration-success-screen"
      >
        <Typography variant="h4" gutterBottom>
          Registration Successful!
        </Typography>
        <Typography variant="body1" color="text.secondary" data-testid="welcome-message">
          Welcome, {firstName}! Your account has been created successfully.
        </Typography>
        <Typography variant="body1" color="text.secondary"  sx={{ mt: 2 }}>
          <strong>
          Please check your email to verify your account.
          </strong>
        </Typography>
       
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate("/login")}
          sx={{ mt: 3 }}
          data-testid="go-to-login-button"
        >
          Go to Login
        </Button>
      </Stack>
    );
  }

  // Registration Form
  return (
    <Stack component="form" className="registration-form-container" onSubmit={handleSubmit} data-testid="registration-form">
      <h1 className="WelcomeHeader">New User Registration</h1>
      <Dialog open={isPhotoModalOpen} onClose={handleClosePhotoModal} className="profile-picture-box">
        <DialogTitle>Add Profile Picture</DialogTitle>
        <DialogContent sx={{ minWidth: "250px", maxWidth: "300px", minHeight: "107px", pt: 1 }}>
          <TextField
            data-testid="update-avatar"
            fullWidth
            label="Image URL"
            variant="outlined"
            value={tempProfile}
            name="profilePictureURL"
            onChange={(e) => {
              setTempProfile(e.target.value);
              setErrors(prev => ({ ...prev, profilePictureURL: validateField("profilePictureURL", e.target.value) }));
            }}
            inputProps={{ maxLength: 2000 }}  
            error={!!errors.profilePictureURL}
            helperText={errors.profilePictureURL}
            placeholder="https://example.com/image.jpg"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePhotoModal}>Cancel</Button>
          <Button sx={{ color: "white", bgcolor: "#6e2e76", "&:hover": { bgcolor: "#5a2561"} }} onClick={handleAvatar} data-testid="save-avatar-url">Done</Button>
        </DialogActions>
      </Dialog>
        <Tooltip className="profile-picture-tooltip" title="Add a profile picture" placement="right" arrow enterDelay={500} enterNextDelay={500} data-testid="type-tooltip"> 
        <Avatar
          src={profilePictureURL}
          onClick={handleOpenPhotoModal}
          sx={{
            bgcolor: "#fff",
            width: 80,
            height: 80,
            border: "5px solid #6e2e76",
            boxShadow: "0 8px 25px rgba(110, 46, 118, 0.3)",
            cursor: "pointer",
          }}
          data-testid="profile-avatar"
        >
          <PersonIcon sx={{ fontSize: 70, color: "#6e2e76"}} />
        </Avatar>
        <FilterRoundedIcon data-testid="profile-picture-edit-icon" onClick={handleOpenPhotoModal} className="filter-rounded-icon"/>
        </Tooltip>
      <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
        <TextField
          id="firstName"
          data-testid="first-name-input"
          label="First Name"
          variant="filled"
          size="small"
          name="firstName"
          sx={{ width: '100%' }}
          placeholder="Enter your First Name"
          required
          value={firstName}
          onChange={handleChange}
          error={!!errors.firstName}
          helperText={errors.firstName}
          inputProps={{ maxLength: 50 }}
        />

        <TextField
          id="lastName"
          data-testid="last-name-input"
          label="Last Name"
          variant="filled"
          size="small"
          name="lastName"
          sx={{ width: '100%' }}
          placeholder="Enter your Last Name"
          required
          value={lastName}
          onChange={handleChange}
          error={!!errors.lastName}
          helperText={errors.lastName}
          inputProps={{ maxLength: 50 }}
        />
      </Stack>

      <TextField
        id="email"
        data-testid="email-input"
        label="Email"
        variant="filled"
        size="small"
        type="email"
        name="email"
        sx={{ width: '100%' }}
        placeholder="Enter your email"
        required
        value={email}
        onChange={handleChange}
        error={!!errors.email}
        helperText={errors.email}
        slotProps={{ htmlInput: { maxLength: 255 } }}
      />

      <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
        <TextField
          id="password"
          data-testid="password-input"
          label="Password"
          variant="filled"
          size="small"
          type={showPassword ? 'text' : 'password'}
          name="password"
          sx={{ width: '100%' }}
          InputProps={{
            endAdornment: passwordAdornment,
          }}
          placeholder="Enter your password"
          required
          value={password}
          onChange={handlePasswordChange}
          error={!!errors.password}
          helperText={errors.password}
        />

        <TextField
          id="confirmPassword"
          data-testid="confirm-password-input"
          label="Confirm password"
          variant="filled"
          size="small"
          type={showConfirmPassword ? 'text' : 'password'}
          name="confirmPassword"
          sx={{ width: '100%' }}
          InputProps={{
            endAdornment: confirmPasswordAdornment,
          }}
          placeholder="Confirm your password"
          required
          value={confirmPassword}
          onChange={handlePasswordChange}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
        />
      </Stack>

      <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
        <TextField
          id="phoneNumber"
          data-testid="phone-number-input"
          label="Phone Number"
          variant="filled" 
          size="small"
          name="phoneNumber"
          sx={{ width: '100%' }}
          type="tel"
          placeholder="(123) 456-7890"
          value={formatPhoneNumber(phoneNumber)}
          onChange={handleChange}
          error={!!errors.phoneNumber}
          helperText={errors.phoneNumber}
          slotProps={{ htmlInput: { maxLength: 14 } }}
        />
      </Stack>
      
      {clientError && (
        <Alert
          severity="error"
          sx={{
            mt: 2,
            whiteSpace: 'pre-line',
            textAlign: 'left'
          }}
          data-testid="client-error-alert"
        >
          {clientError}
        </Alert>
      )}

      <Button
        className="register-button"
        variant="contained"
        type="submit"
        disabled={!isFormValid || loading}
        sx={{
          width: '100%',
          mt: 1
        }}
        data-testid="register-button"
      >
        {loading ? "Creating account..." : "Register"}
      </Button>

      <h3 className="accountHeader" data-testid="already-have-account-text">Already have an account? <a href="/login" data-testid="login-link">Login</a></h3>
    </Stack>
  );
}
 